import { useEffect, useRef, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { Send, Building2 } from "lucide-react";
import { Avatar } from "@/components/Avatar";
import BookingChat from "@/components/BookingChat";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

const DIRECT = "__direct__";

function DirectChat() {
  const [msgs, setMsgs] = useState([]);
  const [body, setBody] = useState("");
  const scroller = useRef(null);
  const load = useCallback(() => api.get("/my/dm").then((r) => { setMsgs(r.data); setTimeout(() => scroller.current?.scrollTo(0, scroller.current.scrollHeight), 50); }).catch(() => {}), []);
  useEffect(() => { load(); const id = setInterval(load, 8000); return () => clearInterval(id); }, [load]);
  const send = async () => {
    const text = body.trim();
    if (!text) return;
    setBody("");
    try { await api.post("/my/dm", { body: text }); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <div className="flex flex-col h-full min-h-[400px]" data-testid="direct-chat">
      <div ref={scroller} className="flex-1 overflow-y-auto space-y-2 mb-3 max-h-[440px]">
        {msgs.length === 0 && <p className="text-slate-400 text-sm text-center py-10">Send us a message — the Belo Rentals team will reply here.</p>}
        {msgs.map((m) => (
          <div key={m.id} className={`flex ${m.sender_role === "user" ? "justify-end" : "justify-start"}`}>
            <div className={`max-w-[75%] rounded-2xl px-4 py-2 text-sm ${m.sender_role === "user" ? "bg-blue-600 text-white" : "bg-white border border-slate-200 text-slate-800"}`}>
              {m.body}
              <span className={`block text-[10px] mt-1 ${m.sender_role === "user" ? "text-blue-100" : "text-slate-400"}`}>{new Date(m.created_at).toLocaleString()}</span>
            </div>
          </div>
        ))}
      </div>
      <div className="flex items-center gap-2">
        <input value={body} onChange={(e) => setBody(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()} data-testid="my-dm-input" placeholder="Message Belo Rentals…" className="belo-input flex-1" />
        <button onClick={send} data-testid="my-dm-send" className="grid place-items-center w-11 h-11 rounded-full bg-blue-600 text-white hover:bg-blue-700 shrink-0"><Send size={16} /></button>
      </div>
    </div>
  );
}

export function CustomerMessages({ conversations, active, setActive }) {
  const { t } = useTranslation();
  const sel = (conversations || []).find((c) => c.booking_id === active) || null;
  return (
    <div className="grid grid-cols-1 md:grid-cols-12 bg-white rounded-2xl border border-slate-200 overflow-hidden min-h-[560px]" data-testid="messages-center">
      <div className="md:col-span-4 border-r border-slate-200 flex flex-col max-h-[560px] overflow-y-auto">
        <button data-testid="conversation-direct" onClick={() => setActive(DIRECT)}
          className={`flex gap-3 items-start text-left p-4 border-b border-slate-100 transition-colors ${active === DIRECT ? "bg-blue-50" : "hover:bg-slate-50"}`}>
          <span className="grid place-items-center w-10 h-10 rounded-full bg-slate-900 text-white shrink-0"><Building2 size={18} /></span>
          <div className="min-w-0 flex-1">
            <p className="font-semibold text-slate-900 text-sm truncate">Belo Rentals</p>
            <p className="text-xs text-slate-500 truncate">Support & general questions</p>
          </div>
        </button>
        {(conversations || []).map((c) => (
          <button key={c.booking_id} data-testid={`conversation-${c.booking_id}`} onClick={() => setActive(c.booking_id)}
            className={`flex gap-3 items-start text-left p-4 border-b border-slate-100 transition-colors ${active === c.booking_id ? "bg-blue-50" : "hover:bg-slate-50"}`}>
            <Avatar name={c.host_name} size="md" />
            <div className="min-w-0 flex-1">
              <p className="font-semibold text-slate-900 text-sm truncate">{c.host_name}</p>
              <p className="text-xs text-slate-500 truncate">{c.car_name}</p>
              <p className="text-xs text-slate-400 truncate mt-0.5">{c.last_message || t("messages.noMessages")}</p>
            </div>
          </button>
        ))}
      </div>
      <div className="md:col-span-8 p-4 bg-slate-50/50" data-testid="message-thread-view">
        {active === DIRECT ? (
          <DirectChat />
        ) : sel ? (
          <BookingChat key={sel.booking_id} bookingId={sel.booking_id} role="customer" />
        ) : (
          <div className="h-full grid place-items-center text-slate-400 text-sm min-h-[400px]">{t("messages.selectConversation")}</div>
        )}
      </div>
    </div>
  );
}
